import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';

import EndOfPageRecommendationTwgQuery from '../__generated__/EndOfPageRecommendationTwgQuery.graphql.ts';
import type {
	EndOfPageRecommendationTwgQuery$data,
	EndOfPageRecommendationTwgQuery$variables,
} from '../__generated__/EndOfPageRecommendationTwgQuery.graphql.ts';
import { transformTwgRecs } from '../twgTransformers.ts';
import {
	MAX_TWG_RECS,
	buildTwgQueryContext,
	buildTwgContentAri,
	buildTwgRecsCypher,
	isTwgSupportedContentType,
} from '../twgQueryConfig.ts';

export type UseTwgRecommendationDataResult = {
	/** Ranked, adapted cards (always an array; empty when there are none). */
	pageCardData: GlobalPageCard[];
	/** Current space id (for analytics parity with the cc-smarts arm). */
	spaceId: string | undefined;
	/** Space-level enablement (admin toggle + default behaviour). */
	isEnabled: boolean;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	/** Whether the query was skipped (unsupported content type / missing context). */
	skip: boolean;
};

/**
 * Single source of truth for the TWG (treatment) end-of-page recommendations:
 * fetches graph recs (inline-hydrated) + space enablement + space id in one AGG
 * round-trip and adapts them to the shared card model. Shared by the rendering
 * container and the `hasRecs` layout hook so both dedupe to one request.
 */
export const useTwgRecommendationData = ({
	entityId,
	entityType,
	spaceKey,
	skip = false,
}: {
	entityId: string;
	entityType: string;
	spaceKey: string;
	skip?: boolean;
}): UseTwgRecommendationDataResult => {
	const { cloudId } = useSessionData();

	const shouldSkip = skip || !isTwgSupportedContentType(entityType) || !cloudId || !entityId;

	const cypherQuery = useMemo(
		() => (shouldSkip ? '' : buildTwgRecsCypher(buildTwgContentAri(cloudId, entityId, entityType))),
		[shouldSkip, cloudId, entityId, entityType],
	);

	const { data, loading, error } = useQueryInterop<
		EndOfPageRecommendationTwgQuery$data,
		EndOfPageRecommendationTwgQuery$variables
	>(EndOfPageRecommendationTwgQuery, {
		variables: {
			cypherQuery,
			first: MAX_TWG_RECS,
			key: spaceKey,
			keyString: spaceKey,
		},
		interopApolloAggClient: true,
		context: shouldSkip
			? undefined
			: { headers: { 'X-Query-Context': buildTwgQueryContext(cloudId) } },
		errorPolicy: 'all' as const,
		// Required: without it the Relay path defaults to network-only and every
		// consumer re-fetches. cache-first lets consumers read the preloaded/store result.
		fetchPolicy: 'cache-first' as const,
		skip: shouldSkip,
	});

	const pageCardData = useMemo(() => transformTwgRecs(data), [data]);

	// Space-level enablement only (admin toggle + default behaviour). The per-user
	// opt-out is intentionally not read here — see the note on the query. Compared as
	// a string literal (typed by the query's own generated union) to avoid coupling to
	// the cc-smarts query's generated enum.
	const isEnabled = Boolean(
		data?.getRecommendedPagesSpaceStatus?.recommendedPagesEnabled &&
		data?.getRecommendedPagesSpaceStatus?.defaultBehavior === 'SHOWN',
	);

	return {
		pageCardData,
		spaceId: data?.space?.id ?? undefined,
		isEnabled,
		loading,
		error,
		skip: shouldSkip,
	};
};
